import React, { useState, useEffect } from 'react';
import { api, useAuth } from '@/App';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Badge } from '@/components/ui/badge';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Textarea } from '@/components/ui/textarea';
import {
  Table, TableBody, TableCell, TableHead, TableHeader, TableRow,
} from '@/components/ui/table';
import {
  Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle,
} from '@/components/ui/dialog';
import { RefreshCw, Search, Eye, CheckCircle2, XCircle, Loader2, Filter, X, Edit2, ChevronDown, ChevronRight, Send } from 'lucide-react';
import { toast } from 'sonner';
import { useLocation, useNavigate } from 'react-router-dom';
import { ShiftLogDetailDialog } from '@/components/ShiftLogDetailDialog';
import { computeToolStats, computeDeviation } from '@/utils/toolLifeCalculations';

const statusColors = {
  Draft: 'bg-slate-100 text-slate-700',
  Submitted: 'bg-blue-100 text-blue-700',
  Approved: 'bg-green-100 text-green-700',
  Rejected: 'bg-red-100 text-red-700',
};

export default function ToolInspectionHistoryPage() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const canApprove = user?.role === 'MANAGER' || user?.role === 'OWNER';
  const canSubmit = user?.role === 'SUPERVISOR' || user?.role === 'MANAGER' || user?.role === 'OWNER';

  const [logs, setLogs] = useState([]);
  const [machines, setMachines] = useState([]);
  const [loading, setLoading] = useState(false);

  const [page, setPage] = useState(1);
  const pageSize = 28;
  const [total, setTotal] = useState(0);
  const totalPages = Math.ceil(total / pageSize);
  const [hasSearched, setHasSearched] = useState(false);

  const [dateFrom, setDateFrom] = useState('');
  const [dateTo, setDateTo] = useState('');
  const [filterMachine, setFilterMachine] = useState('all');
  const [filterShift, setFilterShift] = useState('all');
  const [filterOperation, setFilterOperation] = useState('all');
  const [filterStatus, setFilterStatus] = useState('all');

  const [detailLog, setDetailLog] = useState(null);
  const [detailOpen, setDetailOpen] = useState(false);
  const [combinedPreviewOpen, setCombinedPreviewOpen] = useState(false);
  const [combinedPreviewLogs, setCombinedPreviewLogs] = useState([]);
  const [combinedPreviewDate, setCombinedPreviewDate] = useState('');

  const [actionLog, setActionLog] = useState(null);
  const [actionLogGroup, setActionLogGroup] = useState(null);
  const [actionGroupDate, setActionGroupDate] = useState('');
  const [actionType, setActionType] = useState('');
  const [actionOpen, setActionOpen] = useState(false);
  const [rejectReason, setRejectReason] = useState('');
  const [processing, setProcessing] = useState(false);

  useEffect(() => {
    api.get('/machines')
      .then(res => setMachines(res.data ?? []))
      .catch(() => {
        setMachines([]);
        toast.error('Failed to load machines');
      });
  }, []);

  const fetchLogs = async (pageOverride, statusOverride) => {
    const targetPage = pageOverride || page || 1;
    setLoading(true);
    setHasSearched(true);
    try {
      const params = {};
      if (dateFrom) params.date_from = dateFrom;
      if (dateTo) params.date_to = dateTo;
      if (filterMachine !== 'all') params.machine_id = filterMachine;
      if (filterShift !== 'all') params.shift = filterShift;
      if (filterOperation !== 'all') params.operation = filterOperation;
      const statusToUse = statusOverride || filterStatus;
      if (statusToUse !== 'all') params.status = statusToUse;
      params.page = targetPage;
      params.page_size = pageSize;
      const res = await api.get('/shift-logs', { params });
      const data = Array.isArray(res.data) ? res.data : [];
      setLogs(data);
      const headerCount = Number(res.headers['x-total-count'] || res.headers['X-Total-Count'] || 0);
      setTotal(headerCount || data.length);
      setPage(targetPage);
    } catch {
      toast.error('Failed to load shift logs');
    } finally {
      setLoading(false);
    }
  };

  const clearFilters = () => {
    setDateFrom('');
    setDateTo('');
    setFilterMachine('all');
    setFilterShift('all');
    setFilterOperation('all');
    setFilterStatus('all');
    setLogs([]);
    setTotal(0);
    setPage(1);
    setHasSearched(false);
  };

  useEffect(() => {
    const statusFromState = location.state && location.state.status;
    if (statusFromState && typeof statusFromState === 'string') {
      setFilterStatus(statusFromState);
      fetchLogs(1, statusFromState);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const openDetail = async (logId) => {
    try {
      const res = await api.get('/shift-logs/' + logId);
      setDetailLog(res.data);
      setDetailOpen(true);
    } catch {
      toast.error('Failed to load details');
    }
  };

  const openCombinedPreview = async (date, dateLogs) => {
    setCombinedPreviewDate(date);
    setCombinedPreviewLogs([]);
    setCombinedPreviewOpen(true);
    try {
      const fullLogs = await Promise.all(dateLogs.map(log => api.get('/shift-logs/' + log.id).then(r => r.data)));
      const sorted = fullLogs.sort((a, b) => {
        const shiftOrder = (a.shift || '').localeCompare(b.shift || '');
        if (shiftOrder !== 0) return shiftOrder;
        return (a.machine_code || '').localeCompare(b.machine_code || '');
      });
      setCombinedPreviewLogs(sorted);
    } catch {
      toast.error('Failed to load combined preview');
      setCombinedPreviewOpen(false);
    }
  };

  const handleAction = async () => {
    setProcessing(true);
    try {
      const isGroup = Array.isArray(actionLogGroup) && actionLogGroup.length > 0;
      if (actionType === 'approve') {
        if (isGroup) {
          const promises = actionLogGroup.map((log) =>
            api.patch('/shift-logs/' + log.id + '/approve').then(() => ({ log, ok: true }), (err) => ({ log, ok: false, err }))
          );
          const results = await Promise.allSettled(promises);
          const succeeded = results.filter((r) => r.status === 'fulfilled' && r.value?.ok).length;
          const failed = results.filter((r) => r.status === 'fulfilled' && !r.value?.ok);
          const failedIds = failed.map((r) => r.value?.log?.id).filter(Boolean);
          const total = actionLogGroup.length;
          if (succeeded === total) {
            toast.success('Approved ' + total + ' log(s)');
          } else if (succeeded > 0) {
            toast.warning(
              'Approved ' + succeeded + ' of ' + total + ' log(s). ' + failed.length + ' failed' +
              (failedIds.length ? ' (IDs: ' + failedIds.join(', ') + ')' : '')
            );
          } else {
            const msg = failed[0]?.value?.err?.response?.data?.detail || 'All approve requests failed';
            toast.error(msg);
          }
        } else {
          await api.patch('/shift-logs/' + actionLog.id + '/approve');
          toast.success('Shift log approved');
        }
      } else {
        if (isGroup) {
          const promises = actionLogGroup.map((log) =>
            api.patch('/shift-logs/' + log.id + '/reject', { reason: rejectReason }).then(() => ({ log, ok: true }), (err) => ({ log, ok: false, err }))
          );
          const results = await Promise.allSettled(promises);
          const succeeded = results.filter((r) => r.status === 'fulfilled' && r.value?.ok).length;
          const failed = results.filter((r) => r.status === 'fulfilled' && !r.value?.ok);
          const failedIds = failed.map((r) => r.value?.log?.id).filter(Boolean);
          const total = actionLogGroup.length;
          if (succeeded === total) {
            toast.success('Rejected ' + total + ' log(s)');
          } else if (succeeded > 0) {
            toast.warning(
              'Rejected ' + succeeded + ' of ' + total + ' log(s). ' + failed.length + ' failed' +
              (failedIds.length ? ' (IDs: ' + failedIds.join(', ') + ')' : '')
            );
          } else {
            const msg = failed[0]?.value?.err?.response?.data?.detail || 'All reject requests failed';
            toast.error(msg);
          }
        } else {
          await api.patch('/shift-logs/' + actionLog.id + '/reject', { reason: rejectReason });
          toast.success('Shift log rejected');
        }
      }
      setActionOpen(false);
      setActionLogGroup(null);
      setActionGroupDate('');
      fetchLogs();
      window.dispatchEvent(new CustomEvent('approvals-changed'));
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Action failed');
    } finally {
      setProcessing(false);
    }
  };

  const handleApproveGroup = (date, dateLogs) => {
    const submitted = dateLogs.filter(l => l.status === 'Submitted');
    if (submitted.length === 0) return;
    setActionLogGroup(submitted);
    setActionGroupDate(date);
    setActionType('approve');
    setActionLog(null);
    setActionOpen(true);
  };

  const handleRejectGroup = (date, dateLogs) => {
    const submitted = dateLogs.filter(l => l.status === 'Submitted');
    if (submitted.length === 0) return;
    setActionLogGroup(submitted);
    setActionGroupDate(date);
    setActionType('reject');
    setActionLog(null);
    setRejectReason('');
    setActionOpen(true);
  };

  const handleSubmitGroup = async (date, dateLogs) => {
    const drafts = dateLogs.filter(l => l.status === 'Draft');
    if (drafts.length === 0 || !canSubmit) return;
    const ok = window.confirm(`Submit ${drafts.length} draft log(s) for ${date} for approval?`);
    if (!ok) return;
    try {
      const promises = drafts.map(log =>
        api.patch('/shift-logs/' + log.id + '/submit').then(
          () => ({ ok: true }),
          (err) => ({ ok: false, err })
        )
      );
      const results = await Promise.all(promises);
      const succeeded = results.filter(r => r.ok).length;
      const failed = results.filter(r => !r.ok);
      if (succeeded && !failed.length) {
        toast.success(`Submitted ${succeeded} draft log(s)`);
      } else if (succeeded && failed.length) {
        toast.warning(`Submitted ${succeeded} draft log(s), ${failed.length} failed`);
      } else {
        toast.error('Failed to submit draft logs');
      }
      fetchLogs();
      if (succeeded) window.dispatchEvent(new CustomEvent('approvals-changed'));
    } catch {
      toast.error('Failed to submit draft logs');
    }
  };

  const handleSubmitSingle = async (log) => {
    if (!log?.id || !canSubmit) return;
    const ok = window.confirm('Submit this draft log for approval?');
    if (!ok) return;

    try {
      await api.patch('/shift-logs/' + log.id + '/submit');
      toast.success('Shift log submitted for approval');
      fetchLogs();
      window.dispatchEvent(new CustomEvent('approvals-changed'));
    } catch (err) {
      console.error(err);
      toast.error(err.response?.data?.detail || 'Failed to submit shift log');
    }
  };

  return (
    <div className="space-y-6 animate-fade-in" data-testid="tool-inspection-history-page">
      <div className="flex flex-col md:flex-row md:items-center justify-between gap-4">
        <div>
          <h1 className="font-heading text-3xl font-bold text-slate-900">Tool Inspection History</h1>
          <p className="text-slate-500">View and manage shift logs</p>
        </div>
        <Button variant="outline" onClick={fetchLogs} data-testid="refresh-history-btn">
          <RefreshCw className={'w-4 h-4 mr-2 ' + (loading ? 'animate-spin' : '')} /> Refresh
        </Button>
      </div>

      <FiltersCard
        dateFrom={dateFrom} setDateFrom={setDateFrom}
        dateTo={dateTo} setDateTo={setDateTo}
        filterMachine={filterMachine} setFilterMachine={setFilterMachine}
        filterShift={filterShift} setFilterShift={setFilterShift}
        filterOperation={filterOperation} setFilterOperation={setFilterOperation}
        filterStatus={filterStatus} setFilterStatus={setFilterStatus}
        machines={machines}
        onClear={clearFilters}
        onSearch={() => fetchLogs(1)}
      />

      <LogsTable
        logs={logs}
        loading={loading}
        hasSearched={hasSearched}
        canApprove={canApprove}
        canSubmit={canSubmit}
        onView={openDetail}
        onViewGroup={openCombinedPreview}
        onApproveGroup={handleApproveGroup}
        onRejectGroup={handleRejectGroup}
        onSubmitGroup={handleSubmitGroup}
        onApprove={(log) => { setActionLog(log); setActionLogGroup(null); setActionType('approve'); setRejectReason(''); setActionOpen(true); }}
        onReject={(log) => { setActionLog(log); setActionLogGroup(null); setActionType('reject'); setRejectReason(''); setActionOpen(true); }}
        onSubmit={handleSubmitSingle}
        onEdit={(log) => navigate('/tool-inspection?date=' + log.date + '&shift=' + log.shift)}
      />

      {hasSearched && (
        <div className="flex flex-col sm:flex-row items-center justify-between gap-3 text-sm text-slate-600 mt-2">
          <div className="text-center sm:text-left">
            {total > 0 ? (
              <>
                Showing <span className="font-semibold">{logs.length}</span> of{' '}
                <span className="font-semibold">{total}</span> log
                {total !== 1 ? 's' : ''} (page {page} of {Math.max(1, totalPages)})
              </>
            ) : (
              'No shift logs to display'
            )}
          </div>
          {totalPages > 1 && (
            <div className="flex items-center gap-2">
              <Button
                variant="outline"
                size="sm"
                disabled={page <= 1 || loading}
                onClick={() => fetchLogs(page - 1)}
                data-testid="logs-page-prev"
              >
                Previous
              </Button>
              <span className="text-xs sm:text-sm">
                Page <span className="font-semibold">{page}</span> of{' '}
                <span className="font-semibold">{Math.max(1, totalPages)}</span>
              </span>
              <Button
                variant="outline"
                size="sm"
                disabled={page >= totalPages || loading}
                onClick={() => fetchLogs(page + 1)}
                data-testid="logs-page-next"
              >
                Next
              </Button>
            </div>
          )}
        </div>
      )}

      <ShiftLogDetailDialog log={detailLog} open={detailOpen} onOpenChange={setDetailOpen} />
      <CombinedPreviewDialog
        date={combinedPreviewDate}
        logs={combinedPreviewLogs}
        open={combinedPreviewOpen}
        onOpenChange={setCombinedPreviewOpen}
      />

      <Dialog open={actionOpen} onOpenChange={(open) => { if (!open) { setActionLogGroup(null); setActionGroupDate(''); } setActionOpen(open); }}>
        <DialogContent data-testid="action-dialog">
          <DialogHeader>
            <DialogTitle>{actionType === 'approve' ? 'Approve' : 'Reject'} {actionLogGroup?.length ? 'Shift Logs' : 'Shift Log'}</DialogTitle>
            <DialogDescription>
              {actionLogGroup?.length
                ? `${actionLogGroup.length} log(s) for ${actionGroupDate}`
                : actionLog && (actionLog.machine_code + ' | ' + actionLog.date + ' | ' + actionLog.shift)}
            </DialogDescription>
          </DialogHeader>
          {actionType === 'reject' && (
            <div className="space-y-2">
              <Label>Rejection Reason (optional)</Label>
              <Textarea value={rejectReason} onChange={e => setRejectReason(e.target.value)} placeholder="Enter reason for rejection..." data-testid="reject-reason-input" />
            </div>
          )}
          <DialogFooter>
            <Button variant="outline" onClick={() => setActionOpen(false)}>Cancel</Button>
            <Button
              onClick={handleAction}
              disabled={processing}
              className={actionType === 'approve' ? 'bg-green-600 hover:bg-green-700' : 'bg-red-600 hover:bg-red-700'}
              data-testid="confirm-action-btn"
            >
              {processing && <Loader2 className="w-4 h-4 mr-2 animate-spin" />}
              {actionType === 'approve' ? 'Approve' : 'Reject'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}

function FiltersCard({ dateFrom, setDateFrom, dateTo, setDateTo, filterMachine, setFilterMachine, filterShift, setFilterShift, filterOperation, setFilterOperation, filterStatus, setFilterStatus, machines, onClear, onSearch }) {
  return (
    <Card className="border-slate-200">
      <CardHeader className="pb-3">
        <div className="flex items-center justify-between">
          <CardTitle className="text-base flex items-center gap-2">
            <Filter className="w-4 h-4" /> Filters
          </CardTitle>
          <Button variant="ghost" size="sm" onClick={onClear} data-testid="clear-filters-btn">
            <X className="w-3.5 h-3.5 mr-1" /> Clear
          </Button>
        </div>
      </CardHeader>
      <CardContent>
        <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-6 gap-3">
          <div className="space-y-1">
            <Label className="text-xs">From</Label>
            <Input type="date" value={dateFrom} onChange={e => setDateFrom(e.target.value)} className="h-10 w-40" data-testid="filter-date-from" />
          </div>
          <div className="space-y-1">
            <Label className="text-xs">To</Label>
            <Input type="date" value={dateTo} onChange={e => setDateTo(e.target.value)} className="h-10 w-40" data-testid="filter-date-to" />
          </div>
          <div className="space-y-1">
            <Label className="text-xs">Machine</Label>
            <Select value={filterMachine} onValueChange={setFilterMachine}>
              <SelectTrigger className="h-9 text-sm"><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All</SelectItem>
                {machines.map(m => <SelectItem key={m.id} value={m.id}>{m.code}</SelectItem>)}
              </SelectContent>
            </Select>
          </div>
          <div className="space-y-1">
            <Label className="text-xs">Shift</Label>
            <Select value={filterShift} onValueChange={setFilterShift}>
              <SelectTrigger className="h-9 text-sm"><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All</SelectItem>
                <SelectItem value="Day">Day</SelectItem>
                <SelectItem value="Night">Night</SelectItem>
              </SelectContent>
            </Select>
          </div>
          <div className="space-y-1">
            <Label className="text-xs">Operation</Label>
            <Select value={filterOperation} onValueChange={setFilterOperation}>
              <SelectTrigger className="h-9 text-sm"><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All</SelectItem>
                <SelectItem value="OBR">OBR</SelectItem>
                <SelectItem value="OTR">OTR</SelectItem>
              </SelectContent>
            </Select>
          </div>
          <div className="space-y-1">
            <Label className="text-xs">Status</Label>
            <Select value={filterStatus} onValueChange={setFilterStatus}>
              <SelectTrigger className="h-9 text-sm"><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All</SelectItem>
                <SelectItem value="Draft">Draft</SelectItem>
                <SelectItem value="Submitted">Submitted</SelectItem>
                <SelectItem value="Approved">Approved</SelectItem>
                <SelectItem value="Rejected">Rejected</SelectItem>
              </SelectContent>
            </Select>
          </div>
        </div>
        <div className="mt-3">
          <Button size="sm" onClick={onSearch} data-testid="apply-filters-btn">
            <Search className="w-3.5 h-3.5 mr-1.5" /> Search
          </Button>
        </div>
      </CardContent>
    </Card>
  );
}

function groupLogsByDate(logs) {
  const byDate = {};
  logs.forEach(log => {
    if (!byDate[log.date]) byDate[log.date] = [];
    byDate[log.date].push(log);
  });
  return Object.entries(byDate).sort((a, b) => b[0].localeCompare(a[0]));
}

function LogsTable({ logs, loading, hasSearched, canApprove, canSubmit, onView, onViewGroup, onApproveGroup, onRejectGroup, onSubmitGroup, onApprove, onReject, onSubmit, onEdit }) {
  const [expandedDates, setExpandedDates] = useState(new Set());
  const dateGroups = groupLogsByDate(logs);

  const toggleDate = (date) => {
    setExpandedDates(prev => {
      const next = new Set(prev);
      if (next.has(date)) next.delete(date);
      else next.add(date);
      return next;
    });
  };

  return (
    <Card className="border-slate-200">
      <CardHeader>
        <CardTitle>Shift Logs by Date ({logs.length} entries)</CardTitle>
        <CardDescription>Grouped by date. Expand a row to see or act on individual shift logs.</CardDescription>
      </CardHeader>
      <CardContent className="p-0">
        <Table className="data-table">
          <TableHeader>
            <TableRow className="bg-slate-50">
              <TableHead className="w-10"></TableHead>
              <TableHead>Date</TableHead>
              <TableHead>Shift</TableHead>
              <TableHead>Machine</TableHead>
              <TableHead>Part No</TableHead>
              <TableHead>Operation</TableHead>
              <TableHead>Incharge</TableHead>
              <TableHead>Status</TableHead>
              <TableHead className="w-32">Actions</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {!hasSearched ? (
              <TableRow>
                <TableCell colSpan={9} className="text-center py-8 text-slate-500">
                  Set filters and click <span className="font-semibold">Search</span> to load inspection history.
                </TableCell>
              </TableRow>
            ) : loading ? (
              <TableRow>
                <TableCell colSpan={9} className="text-center py-8">
                  <Loader2 className="w-6 h-6 animate-spin mx-auto" />
                </TableCell>
              </TableRow>
            ) : dateGroups.length === 0 ? (
              <TableRow>
                <TableCell colSpan={9} className="text-center py-8 text-slate-500">No shift logs found</TableCell>
              </TableRow>
            ) : (
              dateGroups.map(([date, dateLogs]) => (
                <DateGroupRow
                  key={date}
                  date={date}
                  dateLogs={dateLogs}
                  expanded={expandedDates.has(date)}
                  onToggle={() => toggleDate(date)}
                  onViewGroup={onViewGroup}
                  onApproveGroup={onApproveGroup}
                  onRejectGroup={onRejectGroup}
                  canApprove={canApprove}
                  canSubmit={canSubmit}
                  onSubmitGroup={onSubmitGroup}
                  onView={onView}
                  onApprove={onApprove}
                  onReject={onReject}
                  onSubmit={onSubmit}
                  onEdit={onEdit}
                />
              ))
            )}
          </TableBody>
        </Table>
      </CardContent>
    </Card>
  );
}

function DateGroupRow({ date, dateLogs, expanded, onToggle, onViewGroup, onApproveGroup, onRejectGroup, onSubmitGroup, canApprove, canSubmit, onView, onApprove, onReject, onSubmit, onEdit }) {
  const shifts = [...new Set(dateLogs.map(l => l.shift))].sort();
  const machines = [...new Set(dateLogs.map(l => l.machine_code))].sort();
  const statusCounts = dateLogs.reduce((acc, l) => { acc[l.status] = (acc[l.status] || 0) + 1; return acc; }, {});
  const statusSummary = Object.entries(statusCounts).map(([s, n]) => `${n} ${s}`).join(', ');
  const submittedCount = dateLogs.filter(l => l.status === 'Submitted').length;
  const draftCount = dateLogs.filter(l => l.status === 'Draft').length;

  const sortedLogs = [...dateLogs].sort((a, b) => {
    const shiftOrder = (a.shift || '').localeCompare(b.shift || '');
    if (shiftOrder !== 0) return shiftOrder;
    return (a.machine_code || '').localeCompare(b.machine_code || '');
  });

  return (
    <>
      <TableRow className="bg-slate-50/80 hover:bg-slate-100 font-medium" data-testid={'date-row-' + date}>
        <TableCell className="w-10 p-0">
          <Button variant="ghost" size="icon" className="h-8 w-8" onClick={onToggle} data-testid={'expand-date-' + date}>
            {expanded ? <ChevronDown className="w-4 h-4" /> : <ChevronRight className="w-4 h-4" />}
          </Button>
        </TableCell>
        <TableCell className="font-mono text-sm">{date}</TableCell>
        <TableCell className="text-sm">{shifts.join(', ')}</TableCell>
        <TableCell className="text-sm font-mono">{machines.join(', ')}</TableCell>
        <TableCell className="text-slate-400">—</TableCell>
        <TableCell className="text-slate-400">—</TableCell>
        <TableCell className="text-slate-400">—</TableCell>
        <TableCell className="text-sm">{dateLogs.length} log(s): {statusSummary}</TableCell>
        <TableCell>
          <div className="flex gap-1">
            <Button variant="ghost" size="icon" className="h-8 w-8" onClick={() => onViewGroup(date, dateLogs)} title="Combined preview" data-testid={'view-group-' + date}>
              <Eye className="w-4 h-4" />
            </Button>
            {canSubmit && draftCount > 0 && (
              <Button
                variant="ghost"
                size="icon"
                className="h-8 w-8 text-blue-600"
                onClick={() => onSubmitGroup(date, dateLogs)}
                title="Submit all draft"
                data-testid={'submit-group-' + date}
              >
                <Send className="w-4 h-4" />
              </Button>
            )}
            {canApprove && submittedCount > 0 && (
              <>
                <Button variant="ghost" size="icon" className="h-8 w-8 text-green-600" onClick={() => onApproveGroup(date, dateLogs)} title="Approve all submitted" data-testid={'approve-group-' + date}>
                  <CheckCircle2 className="w-4 h-4" />
                </Button>
                <Button variant="ghost" size="icon" className="h-8 w-8 text-red-600" onClick={() => onRejectGroup(date, dateLogs)} title="Reject all submitted" data-testid={'reject-group-' + date}>
                  <XCircle className="w-4 h-4" />
                </Button>
              </>
            )}
          </div>
        </TableCell>
      </TableRow>
      {expanded && sortedLogs.map(log => (
        <LogRow key={log.id} log={log} canApprove={canApprove} canSubmit={canSubmit} onView={onView} onApprove={onApprove} onReject={onReject} onSubmit={onSubmit} onEdit={onEdit} isChild />
      ))}
    </>
  );
}

function cpGetStandardRange(standards, partId, toolCode, insertType, operation) {
  if (!standards?.length || !partId || !toolCode || !insertType) return null;
  const std = standards.find(s =>
    s.part_id === partId &&
    s.tool_code === toolCode.toUpperCase() &&
    s.insert_type_code === insertType.toUpperCase()
  );
  if (!std) return null;
  if (operation === 'OTR') {
    return { min: std.frequency_cnc_ind_setup_min, max: std.frequency_cnc_ind_setup_max };
  }
  return { min: std.frequency_1st_setup_min, max: std.frequency_1st_setup_max };
}

function CpDeviationBadge({ deviation }) {
  if (!deviation) return <span className="text-slate-300">-</span>;
  if (deviation.status === 'ok') return <span className="text-green-600 font-medium">OK</span>;
  if (deviation.status === 'below') return <span className="text-red-600 font-medium">{deviation.value}</span>;
  return <span className="text-amber-600 font-medium">+{deviation.value}</span>;
}

function CombinedPreviewDialog({ date, logs, open, onOpenChange }) {
  const [standards, setStandards] = useState([]);

  useEffect(() => {
    if (!open) { setStandards([]); return; }
    api.get('/tool-life-standards').then(res => setStandards(res.data || [])).catch(() => setStandards([]));
  }, [open]);

  const detailStatusColors = {
    Draft: 'bg-slate-100 text-slate-700',
    Submitted: 'bg-blue-100 text-blue-700',
    Approved: 'bg-green-100 text-green-700',
    Rejected: 'bg-red-100 text-red-700',
  };
  function cpCheckpointLife(checkpoints, ci) {
    if (ci < 0 || !checkpoints?.length || ci >= checkpoints.length - 1) return null;
    return Math.abs((checkpoints[ci + 1].qty ?? 0) - (checkpoints[ci].qty ?? 0));
  }
  function CombinedToolSection({ tool, standardRange }) {
    const stats = computeToolStats(tool.checkpoints || []);
    const avgDeviation = computeDeviation(stats.avgLife, standardRange);
    return (
      <div className="border border-slate-200 rounded-lg p-3 space-y-2 bg-slate-50/50">
        <div className="font-medium text-sm">
          Tool {tool.tool_code} {tool.insert_type && '\u2014 ' + tool.insert_type}
        </div>
        {(tool.checkpoints || []).length > 0 && (
          <Table>
            <TableHeader>
              <TableRow className="bg-slate-100/50">
                <TableHead className="text-xs">#</TableHead>
                <TableHead className="text-xs">QTY</TableHead>
                <TableHead className="text-xs">Phase</TableHead>
                <TableHead className="text-xs">Phase Life</TableHead>
                <TableHead className="text-xs">Std Range</TableHead>
                <TableHead className="text-xs">Deviation</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {(tool.checkpoints || []).map((cp, ci) => {
                const life = cpCheckpointLife(tool.checkpoints, ci);
                const deviation = computeDeviation(life, standardRange);
                const phase = cp.insert_phase;
                const totalPhases = cp.total_phases ?? 6;
                const phaseDisplay = phase == null || phase === '' ? '—' : phase === 1 ? 'N' : `${phase}/${totalPhases}`;
                return (
                  <TableRow key={ci}>
                    <TableCell className="text-xs py-1">{ci + 1}</TableCell>
                    <TableCell className="font-mono text-sm py-1">{cp.qty}</TableCell>
                    <TableCell className="text-sm py-1">{phaseDisplay}</TableCell>
                    <TableCell className="font-mono text-sm py-1">{life !== null ? life : '-'}</TableCell>
                    <TableCell className="py-1">
                      <span className="text-xs font-mono text-slate-500">
                        {standardRange ? `${standardRange.min}\u2013${standardRange.max}` : <span className="text-slate-300">N/A</span>}
                      </span>
                    </TableCell>
                    <TableCell className="py-1 text-xs font-mono">
                      <CpDeviationBadge deviation={deviation} />
                    </TableCell>
                  </TableRow>
                );
              })}
            </TableBody>
          </Table>
        )}
        <div className="flex gap-4 text-xs text-slate-600 pt-1 border-t border-slate-100">
          <span>Inserts Used: <strong>{stats.totalInserts}</strong></span>
          <span className="flex items-center gap-1.5">
            Avg Phase Life: <strong>{stats.avgLife !== null ? stats.avgLife.toFixed(1) : '-'}</strong> pcs
            {avgDeviation && <CpDeviationBadge deviation={avgDeviation} />}
          </span>
        </div>
        {standardRange && (
          <div className="text-xs text-slate-500">
            Standard: {standardRange.min}{'\u2013'}{standardRange.max} pcs
          </div>
        )}
      </div>
    );
  }
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-2xl max-h-[85vh] overflow-y-auto" data-testid="combined-preview-dialog">
        <DialogHeader>
          <DialogTitle>Combined preview</DialogTitle>
          <DialogDescription>{date} – all shift logs for this date</DialogDescription>
        </DialogHeader>
        <div className="space-y-6">
          {logs.length === 0 ? (
            <div className="flex items-center justify-center py-8">
              <Loader2 className="w-6 h-6 animate-spin text-slate-400" />
            </div>
          ) : (
            logs.map((log) => (
              <div key={log.id} className="border border-slate-200 rounded-lg p-4 bg-white space-y-3">
                <div className="font-medium text-slate-800 border-b border-slate-100 pb-2">
                  {log.machine_code} | {log.shift} Shift
                </div>
                <div className="grid grid-cols-2 gap-3 text-sm">
                  <div><span className="text-slate-500">Part No:</span> <strong>{log.part_no || '-'}</strong></div>
                  <div>
                    <span className="text-slate-500">Operation: </span>
                    <Badge variant="outline" className={log.operation === 'OBR' ? 'bg-blue-50 text-blue-700' : 'bg-amber-50 text-amber-700'}>{log.operation}</Badge>
                  </div>
                  <div>
                    <span className="text-slate-500">Status: </span>
                    <Badge variant="outline" className={detailStatusColors[log.status]}>{log.status}</Badge>
                  </div>
                  <div><span className="text-slate-500">Shift Incharge:</span> <strong>{log.shift_incharge || '-'}</strong></div>
                </div>
                {log.rejection_reason && (
                  <div className="bg-red-50 border border-red-200 rounded-lg p-2 text-sm">
                    <span className="font-medium text-red-700">Rejection Reason:</span> {log.rejection_reason}
                  </div>
                )}
                {(log.tools || []).map((tool, ti) => (
                  <CombinedToolSection
                    key={ti}
                    tool={tool}
                    standardRange={cpGetStandardRange(standards, log.part_no_id, tool.tool_code, tool.insert_type, log.operation)}
                  />
                ))}
                {log.summary?.insert_counts?.length > 0 && (
                  <div className="text-sm pt-1 border-t border-slate-100">
                    <strong className="text-slate-700">Insert summary:</strong>
                    <div className="flex gap-3 mt-1 flex-wrap">
                      {log.summary.insert_counts.map((ic, i) => {
                        const used = ic.quantity_used ?? ic.quantity_issued ?? 0;
                        const issued = ic.quantity_issued ?? 0;
                        const hasIssued = typeof ic.quantity_used === 'number';
                        return (
                          <span key={i} className="font-mono">
                            {ic.insert_type_code}: {hasIssued ? `issued ${issued}, used ${used}, remaining ${Math.max(0, issued - used)}` : `used ${used}`}
                          </span>
                        );
                      })}
                    </div>
                  </div>
                )}
              </div>
            ))
          )}
        </div>
      </DialogContent>
    </Dialog>
  );
}

function LogRow({ log, canApprove, canSubmit, onView, onApprove, onReject, onSubmit, onEdit, isChild }) {
  const showEdit = canSubmit && (log.status === 'Draft' || log.status === 'Rejected');
  return (
    <TableRow className={isChild ? 'bg-white hover:bg-slate-50/50' : 'hover:bg-slate-50'} data-testid={'log-row-' + log.id}>
      {isChild && <TableCell className="w-10 bg-slate-50/50" />}
      <TableCell className="font-mono text-sm">{log.date}</TableCell>
      <TableCell>{log.shift}</TableCell>
      <TableCell className="font-mono font-medium">{log.machine_code}</TableCell>
      <TableCell className="font-mono">{log.part_no || '-'}</TableCell>
      <TableCell>
        <Badge variant="outline" className={log.operation === 'OBR' ? 'bg-blue-50 text-blue-700' : 'bg-amber-50 text-amber-700'}>{log.operation}</Badge>
      </TableCell>
      <TableCell className="text-sm">{log.shift_incharge}</TableCell>
      <TableCell>
        <Badge variant="outline" className={statusColors[log.status] || ''}>{log.status}</Badge>
      </TableCell>
      <TableCell>
        <div className="flex gap-1">
          <Button variant="ghost" size="icon" className="h-8 w-8" onClick={() => onView(log.id)} data-testid={'view-log-' + log.id}>
            <Eye className="w-4 h-4" />
          </Button>
          {canSubmit && log.status === 'Draft' && (
            <Button
              variant="ghost"
              size="icon"
              className="h-8 w-8 text-blue-600"
              onClick={() => onSubmit(log)}
              data-testid={'submit-log-' + log.id}
            >
              <Send className="w-4 h-4" />
            </Button>
          )}
          {canApprove && log.status === 'Submitted' && (
            <>
              <Button variant="ghost" size="icon" className="h-8 w-8 text-green-600" onClick={() => onApprove(log)} data-testid={'approve-log-' + log.id}>
                <CheckCircle2 className="w-4 h-4" />
              </Button>
              <Button variant="ghost" size="icon" className="h-8 w-8 text-red-600" onClick={() => onReject(log)} data-testid={'reject-log-' + log.id}>
                <XCircle className="w-4 h-4" />
              </Button>
            </>
          )}
          {showEdit && (
            <Button variant="ghost" size="icon" className="h-8 w-8 text-blue-600" onClick={() => onEdit(log)} data-testid={'edit-log-' + log.id}>
              <Edit2 className="w-4 h-4" />
            </Button>
          )}
        </div>
      </TableCell>
    </TableRow>
  );
}
